import { FadeUp, AnimatedLine } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const AboutPage = () => {
  const industrySectors = [
    { name: "Investment & Management", region: "UAE" },
    { name: "Film & Audio Post-Production", region: "UK" },
    { name: "Film Processing & Media", region: "UK" },
    { name: "Film Production & Casting", region: "UK" },
    { name: "Automotive Services", region: "UK" },
    { name: "Hospitality & Dining", region: "UK" },
    { name: "Poultry Breeding & Production", region: "Pakistan" },
    { name: "Agricultural Development", region: "Pakistan" },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">About</span>
            <h1 className="text-display text-charcoal max-w-5xl">
              2 ON Capital
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 max-w-3xl">
            <p className="text-xl md:text-2xl font-sans font-light text-stone leading-relaxed">
              A diversified holding group operating across media production, automotive services, 
              hospitality, agriculture, and investment management.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Group Overview */}
      <section className="section-spacing bg-white grain">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <FadeUp>
                <span className="text-caption text-bronze mb-4 block">01</span>
                <h2 className="text-headline text-charcoal">
                  Group Overview
                </h2>
              </FadeUp>
            </div>

            <div className="lg:col-span-7 lg:col-start-6 space-y-8">
              <FadeUp delay={0.1}>
                <p className="text-body text-lg">
                  Our group operates across media production, automotive services, hospitality, 
                  logistics, agriculture, and investment management. Each business operates 
                  independently within its specialist sector while contributing to a diversified 
                  commercial portfolio.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body">
                  We deliver professional services across the UK and international markets, 
                  combining operational excellence with strategic capital deployment to build 
                  sustainable businesses that create long-term value.
                </p>
              </FadeUp>

              <FadeUp delay={0.3}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  <div className="p-4 bg-sand/30 text-center">
                    <span className="text-2xl font-serif text-bronze">8</span>
                    <p className="text-xs text-stone mt-1">Sectors</p>
                  </div>
                  <div className="p-4 bg-sand/30 text-center">
                    <span className="text-2xl font-serif text-bronze">3</span>
                    <p className="text-xs text-stone mt-1">Regions</p>
                  </div>
                  <div className="p-4 bg-sand/30 text-center">
                    <span className="text-2xl font-serif text-bronze">6</span>
                    <p className="text-xs text-stone mt-1">Industries</p>
                  </div>
                  <div className="p-4 bg-sand/30 text-center">
                    <span className="text-2xl font-serif text-bronze">∞</span>
                    <p className="text-xs text-stone mt-1">Horizon</p>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Dubai Headquarters */}
      <section className="section-spacing">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <FadeUp>
                <div className="aspect-[4/3] relative overflow-hidden border-glass">
                  <img
                    src="https://images.unsplash.com/photo-1770546597346-adb6f350812c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzYW5kJTIwZHVuZXMlMjBtaW5pbWFsJTIwYmVpZ2V8ZW58MHx8fHwxNzcwNzY2OTY4fDA&ixlib=rb-4.1.0&q=85"
                    alt="Dubai"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-bronze/5" />
                </div>
              </FadeUp>
            </div>

            <div className="lg:col-span-5 lg:col-start-8 order-1 lg:order-2">
              <FadeUp>
                <span className="text-caption text-bronze mb-4 block">02</span>
                <h2 className="text-headline text-charcoal mb-8">
                  Dubai Headquarters
                </h2>
              </FadeUp>

              <FadeUp delay={0.1}>
                <p className="text-body text-lg mb-6">
                  2 ON Capital is headquartered in Dubai, operating as a knowledge-based 
                  investment and management firm providing strategic advisory and operational 
                  support to small and medium-sized businesses.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body mb-6">
                  We deliver tailored investment insights, project evaluation, 
                  capital advisory, and commercial management services supported by sector 
                  expertise from approved professional partners.
                </p>
              </FadeUp>

              <FadeUp delay={0.3}>
                <Link
                  to="/portfolio/uae"
                  className="inline-flex items-center gap-2 text-bronze hover:text-charcoal transition-colors duration-300"
                >
                  <span className="text-sm font-sans uppercase tracking-wider">Explore UAE Operations</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Operating Regions */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <FadeUp>
                <span className="text-caption text-bronze mb-4 block">03</span>
                <h2 className="text-headline text-champagne">
                  Global Operations
                </h2>
              </FadeUp>
            </div>

            <div className="lg:col-span-7 lg:col-start-6">
              <FadeUp delay={0.1}>
                <p className="text-xl font-sans font-light text-champagne/80 mb-12">
                  Our portfolio operates across three strategic regions, 
                  each focused on distinct sectors aligned with local market opportunities.
                </p>
              </FadeUp>

              <div className="space-y-6">
                <FadeUp delay={0.2}>
                  <AnimatedLine className="bg-champagne/20 mb-6" />
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                    <div>
                      <span className="font-serif text-xl text-champagne">United Arab Emirates</span>
                      <p className="text-sm text-champagne/60 mt-1">Investment & Commercial Management</p>
                    </div>
                    <Link 
                      to="/portfolio/uae" 
                      className="text-bronze hover:text-champagne transition-colors text-sm inline-flex items-center gap-2"
                    >
                      Explore <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </FadeUp>

                <FadeUp delay={0.3}>
                  <AnimatedLine className="bg-champagne/20 mb-6" />
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                    <div>
                      <span className="font-serif text-xl text-champagne">United Kingdom</span>
                      <p className="text-sm text-champagne/60 mt-1">Media Production, Automotive & Hospitality</p>
                    </div>
                    <Link 
                      to="/portfolio/uk" 
                      className="text-bronze hover:text-champagne transition-colors text-sm inline-flex items-center gap-2"
                    >
                      Explore <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </FadeUp>

                <FadeUp delay={0.4}>
                  <AnimatedLine className="bg-champagne/20 mb-6" />
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                    <div>
                      <span className="font-serif text-xl text-champagne">Pakistan</span>
                      <p className="text-sm text-champagne/60 mt-1">Poultry Farming & Agricultural Development</p>
                    </div>
                    <Link 
                      to="/portfolio/pakistan" 
                      className="text-bronze hover:text-champagne transition-colors text-sm inline-flex items-center gap-2"
                    >
                      Explore <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </FadeUp>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Sectors */}
      <section className="section-spacing">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-4 block">04</span>
            <h2 className="text-headline text-charcoal mb-12">
              Industry Sectors
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industrySectors.map((sector, index) => (
              <FadeUp key={sector.name} delay={index * 0.05}>
                <div className="border-glass p-6 h-full transition-all duration-500">
                  <span className="text-mono text-stone/50 block mb-2">{sector.region}</span>
                  <h3 className="text-lg font-serif text-charcoal">{sector.name}</h3>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Link */}
      <section className="section-spacing bg-sand/30">
        <div className="container-wide">
          <div className="max-w-4xl">
            <FadeUp>
              <span className="text-caption text-bronze mb-4 block">05</span>
              <h2 className="text-headline text-charcoal mb-12">
                Our Approach
              </h2>
            </FadeUp>

            <div className="space-y-8">
              <FadeUp delay={0.1}>
                <p className="text-body text-lg">
                  Each business operates independently within its specialist sector while 
                  contributing to a diversified commercial portfolio delivering professional 
                  services across the UK and international markets.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body">
                  This structure allows each operation to maintain focus and deep expertise 
                  in their respective industries while benefiting from the strategic support, 
                  resources, and governance framework of the broader group.
                </p>
              </FadeUp>
            </div>

            <FadeUp delay={0.3} className="mt-12">
              <Link
                to="/philosophy"
                className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
                data-testid="read-philosophy-link"
              >
                <span className="text-sm font-sans font-medium uppercase tracking-wider">Read Our Investment Philosophy</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
